import React from 'react';
import {
  getBreadCrumbSchema,
  TAB_GOLD_SILVER,
} from '../../../constants/stocks/componentStaticData';
import { handleTrack } from '../../../utils/tracker';
import { Breadcrumb, BREADCRUMB_TYPE } from '../../Atoms/BreadCrumb';
import { TopCTA } from '../../cms/layout/components';
import { Table } from '../../Molecules/table';
import { GoldChart } from '../common/Chart';
import {
  GRAM_WISE_SINGLE_CARAT_COLUMN,
  LAST_10_DAYS_GOLD_RATE_COLUMN,
} from '../common/GoldTable/GoldColumns';
import { LinkTabs } from '../common/LinkedTabs';
import { GoldRateCard } from '../common/RateCard/GoldRateCard';
import { Sidebar } from '../common/Sidebar';
import { ALL_CITIES, GOLD_CITY, METALS, CaratChange } from '../common/types';
import { capitalized, getGramWiseGoldCaratData } from '../common/utils';
import { GoldContent } from './Content';
import { useState } from 'react';

interface GOLD_CITY_HOMEPAGE_PROPS {
  goldData: GOLD_CITY;
  cityData: ALL_CITIES;
  city: { type: string; name: string };
  graphData: number[][];
  metal: METALS;
  top_cta: string;
}

const GoldCityHomepage = ({
  goldData,
  cityData,
  city,
  graphData,
  metal,
  top_cta,
}: GOLD_CITY_HOMEPAGE_PROPS): JSX.Element => {
  const gramWise24CaratData = getGramWiseGoldCaratData(goldData.today, '24k');
  const gramWise22CaratData = getGramWiseGoldCaratData(goldData.today, '22k');
  const gramWise18CaratData = getGramWiseGoldCaratData(goldData.today, '18k');
  const gramWise14CaratData = getGramWiseGoldCaratData(goldData.today, '14k');
  const gramWise12CaratData = getGramWiseGoldCaratData(goldData.today, '12k');
  const gramWise9CaratData = getGramWiseGoldCaratData(goldData.today, '9k');

  const last10DaysColumns = LAST_10_DAYS_GOLD_RATE_COLUMN(10);
  const capitalizedCityName = capitalized(city.name);
  const [change, setChange] = useState<CaratChange>({
    change22k: 0,
    change24k: 0,
  });
  const [percentageChange, setPercentageChange] = useState<CaratChange>({
    change22k: 0,
    change24k: 0,
  });

  const captureEvent = (e: React.MouseEvent<Element>) => {
    handleTrack({
      label: 'top_cta',
      obj: e.target,
    });
  };
  const BREADCRUMB: Array<BREADCRUMB_TYPE> = [
    { title: 'Home', link: '/' },
    { title: 'Gold Rate Today', link: `/${metal}-rate-india` },
    { title: `Gold rate in ${capitalizedCityName}`, link: null },
  ];

  return (
    <div className="mt-[87px] sm:mt-0 sm:px-4 ">
      <LinkTabs
        buttonData={TAB_GOLD_SILVER}
        activeValue={metal}
        city={city.name}
      />

      <div className="mt-16 sm:mt-4">
        <TopCTA CTA={top_cta || ''} onClickCB={captureEvent} />
      </div>
      <div className="pt-4 wg-container">
        <div className="py-4">
          <Breadcrumb dataSet={BREADCRUMB} />
        </div>

        <div className="flex flex-row-reverse gap-10">
          <div className="w-1/4 sm:hidden">
            <Sidebar city={city.name} cityData={cityData} metal={metal} />
          </div>
          <div className="w-3/4 sm:w-full">
            <section className="pb-2">
              <GoldRateCard
                data={goldData.today}
                cities={[
                  ...cityData.country,
                  ...cityData.major_city,
                  ...cityData.metro_city,
                ]}
                selectedCity={city.name}
                change={change}
                percentageChange={percentageChange}
              />
            </section>

            <section className="my-2">
              <h2 className="text-s-28 font-bold text-font-500 sm:text-s-20">
                Gold Price Chart and Trend in {capitalizedCityName}
              </h2>

              <div className="py-6">
                <GoldChart
                  data={graphData}
                  city={city.name}
                  setChange={setChange}
                  setPercentageChange={setPercentageChange}
                />
              </div>
            </section>

            <section className="mt-4 mb-8">
              <h2 className="text-s-28 font-bold text-font-500 sm:text-s-20">
                24 Carat Gold Price in {capitalizedCityName} Today
              </h2>
              <div className="py-6">
                <Table
                  columns={GRAM_WISE_SINGLE_CARAT_COLUMN}
                  tableId={'gramwise24CaratTable'}
                  data={gramWise24CaratData}
                  viewMoreToggle={false}
                  smallWidth={true}
                />
              </div>
            </section>

            <section className="mt-4 mb-8">
              <h2 className="text-s-28 font-bold text-font-500 sm:text-s-20">
                22 Carat Gold Price in {capitalizedCityName} Today
              </h2>
              <div className="py-6">
                <Table
                  columns={GRAM_WISE_SINGLE_CARAT_COLUMN}
                  tableId={'gramwise22CaratTable'}
                  data={gramWise22CaratData}
                  viewMoreToggle={false}
                  smallWidth={true}
                />
              </div>
            </section>

            <section className="mt-4 mb-8">
              <h2 className="text-s-28 font-bold text-font-500 sm:text-s-20">
                18 Carat Gold Price in {capitalizedCityName} Today
              </h2>
              <div className="py-6">
                <Table
                  columns={GRAM_WISE_SINGLE_CARAT_COLUMN}
                  tableId={'gramwise18CaratTable'}
                  data={gramWise14CaratData}
                  viewMoreToggle={false}
                  smallWidth={true}
                />
              </div>
            </section>

            <section className="mt-4 mb-8">
              <h2 className="text-s-28 font-bold text-font-500 sm:text-s-20">
                14 Carat Gold Price in {capitalizedCityName} Today
              </h2>
              <div className="py-6">
                <Table
                  columns={GRAM_WISE_SINGLE_CARAT_COLUMN}
                  tableId={'gramwise18CaratTable'}
                  data={gramWise12CaratData}
                  viewMoreToggle={false}
                  smallWidth={true}
                />
              </div>
            </section>

            <section className="mt-4 mb-8">
              <h2 className="text-s-28 font-bold text-font-500 sm:text-s-20">
                12 Carat Gold Price in {capitalizedCityName} Today
              </h2>
              <div className="py-6">
                <Table
                  columns={GRAM_WISE_SINGLE_CARAT_COLUMN}
                  tableId={'gramwise18CaratTable'}
                  data={gramWise9CaratData}
                  viewMoreToggle={false}
                  smallWidth={true}
                />
              </div>
            </section>

            <section className="mt-4 mb-8">
              <h2 className="text-s-28 font-bold text-font-500 sm:text-s-20">
                9 Carat Gold Price in {capitalizedCityName} Today
              </h2>
              <div className="py-6">
                <Table
                  columns={GRAM_WISE_SINGLE_CARAT_COLUMN}
                  tableId={'gramwise18CaratTable'}
                  data={gramWise18CaratData}
                  viewMoreToggle={false}
                  smallWidth={true}
                />
              </div>
            </section>

            <section className="my-8">
              <h2 className="text-s-28 font-bold text-font-500 sm:text-s-20">
                Gold Rate in {capitalizedCityName} for Last 10 Days (per 10 g)
              </h2>
              <div className="py-6">
                <Table
                  columns={last10DaysColumns}
                  tableId={'testTable2'}
                  data={goldData.last_ten_days_data}
                  viewMoreToggle={false}
                />
              </div>
            </section>

            <GoldContent />
          </div>

          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(getBreadCrumbSchema(BREADCRUMB)),
            }}
          ></script>
        </div>
      </div>
    </div>
  );
};

export default GoldCityHomepage;
